import React, { useState, useRef, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

interface PlatformTermsFile {
  label: string
  href: string
}

interface MenuPosition {
  bottom: number
  left: number
}

const PlatformTerms = (): JSX.Element => {
  const { t } = useTranslation()
  const files = (t('platform_terms.files', { returnObjects: true }) as PlatformTermsFile[]) ?? []
  const [isOpen, setIsOpen] = useState(false)
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  useLayoutEffect(() => {
    if (isOpen && buttonRef.current !== null) {
      const rect = buttonRef.current.getBoundingClientRect()
      setMenuPosition({
        bottom: window.innerHeight - rect.bottom,
        left: rect.right + 4
      })
    } else {
      setMenuPosition(null)
    }
  }, [isOpen])

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => {
          setIsOpen((prev) => !prev)
        }}
        className="border-t border-borders-border-base w-[calc(100%+30px)] ml-[-15px] px-7 flex items-center justify-start gap-2 cursor-pointer bg-transparent border-l-0 border-r-0 border-b-0 text-left hover:opacity-80">
        <div className="justify-start text-foregrounds-fg-subtle text-xs font-medium font-['Montserrat'] leading-4 mt-2">
          {t('platform_terms.title')}
        </div>
        <svg
          className="shrink-0 text-foregrounds-fg-subtle mt-2"
          xmlns="http://www.w3.org/2000/svg"
          width="7"
          height="7"
          viewBox="0 0 7 4"
          style={{ transform: 'rotate(-90deg)' }}>
          <path
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12,13,9.5,11h5Z"
            transform="translate(-8.5 -10)"
          />
        </svg>
      </button>

      {isOpen &&
        menuPosition !== null &&
        createPortal(
          <>
            <div
              role="presentation"
              className="fixed inset-0 z-[100]"
              onClick={() => {
                setIsOpen(false)
              }}
            />
            <div
              className="fixed z-[200] bg-[white] min-w-[12rem] py-1 px-1 bg-backgrounds-bg-component rounded-lg border border-borders-border-base flex flex-col shadow-lg"
              style={{
                bottom: menuPosition.bottom,
                left: menuPosition.left,
                boxShadow:
                  '0px 8px 16px 0px rgba(0,0,0,0.08), 0px 4px 8px 0px rgba(0,0,0,0.08), 0px 0px 0px 1px rgba(0,0,0,0.08)'
              }}
              aria-label="Platform terms – files to download">
              {files.map((file) => (
                <a
                  key={file.label}
                  href={file.href}
                  download
                  className="self-stretch px-2 py-1.5 rounded hover:bg-backgrounds-bg-component-hover flex justify-start items-center gap-2 cursor-pointer no-underline text-foregrounds-fg-base text-xs font-medium font-['Montserrat'] leading-5">
                  {file.label}
                </a>
              ))}
              <div className="px-2 py-1.5 border-t border-borders-border-base shrink-0">
                <span className="text-foregrounds-fg-subtle text-[10px] font-medium font-['Montserrat'] uppercase tracking-wide">
                  {t('platform_terms.title')}
                </span>
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  )
}

export default PlatformTerms
